import React, { useState } from 'react';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { Button, Box, Typography, Alert } from '@mui/material';
import stripeService from '../../services/stripe';
import { useAuth } from 'context/Auth';

interface Props {
  customerId: string;
  onSuccess: () => void;
  onCancel: () => void;
}

const UpdatePaymentMethodForm = ({ customerId, onSuccess, onCancel }: Props) => {
  const stripe = useStripe();
  const elements = useElements();
  const { refreshUser } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!stripe || !elements) return;

    setProcessing(true);
    setError(null);

    try {
      // Create a new payment method
      const { error: pmError, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: elements.getElement(CardElement)!,
      });

      if (pmError) {
        throw new Error(pmError.message);
      }

      // Attach the payment method to the customer
      await stripeService.attachPaymentMethod(paymentMethod.id, customerId);

      // Set as default payment method
      await stripeService.setDefaultPaymentMethod(customerId, paymentMethod.id);

      setSuccess(true);

      // Refresh user data to reflect changes
      await refreshUser();

      // Notify parent component
      setTimeout(() => {
        onSuccess();
      }, 1500);

    } catch (e: any) {
      console.error('Error updating payment method:', e);
      setError(e?.response?.data?.error || e.message || 'An unexpected error occurred');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Update Payment Method
        </Typography>
        <Typography variant="body2" gutterBottom color="text.secondary">
          Enter your new credit card information below. This will be used for all future charges.
        </Typography>
      </Box>

      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Payment method updated successfully!
        </Alert>
      )}

      <Box sx={{ mb: 3 }}>
        <CardElement
          options={{
            style: {
              base: {
                fontSize: '16px',
                color: '#424770',
                '::placeholder': {
                  color: '#aab7c4',
                },
              },
              invalid: {
                color: '#9e2146',
              },
            },
          }}
        />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Button
          type="button"
          variant="outlined"
          onClick={onCancel}
          disabled={processing}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="contained"
          disabled={!stripe || processing || success}
        >
          {processing ? 'Processing...' : 'Update Payment Method'}
        </Button>
      </Box>
    </form>
  );
};

export default UpdatePaymentMethodForm; 